import { useMemo, useState } from 'react';

import dayjs from 'dayjs';
import useSWRInfinite from 'swr/infinite';

// Utils
import { get } from '@/utils/client';
import { getDatePeriods } from '@/utils/datepicker/periods';
import { useDatepickerGlobalStore } from '@/utils/zustand/providers/datepicker-global-provider';

import type { XMediaV2Type } from '@/components/pages/content/types';

import { CUSTOM } from '@/lib/periods';
// API Endpoints
import { MEDIAS_V2 } from '@/lib/routes/apiEndpoints';

type MediaV2RequestParams = {
    social_profile_ids?: number[];
    album_id?: number;
    with_labels?: boolean;
    with_albums?: boolean;
    per_page?: number;
    cursor?: string;
    start?: string;
    end?: string;
    // V2 API params
    post_types?: string[];
    promoted?: boolean;
    labels?: string[];
    hashtags?: string[];
    sort?: { column?: string; order?: 'asc' | 'desc' };
    // Legacy filter params from existing filter components (will be transformed)
    filter_post_type?: string[];
    filter_promotion_type?: 'promoted' | 'non_promoted' | 'all';
    filter_labels?: string[];
    [key: string]: unknown; // Allow other filter params
};

// API response structure from v2 endpoint
type MediaV2ApiResponse = {
    data: XMediaV2Type[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        to: number;
        total: number;
    };
};

// Transformed response structure that matches what PostsList expects
type MediaV2Response = {
    medias: XMediaV2Type[];
    cursor?: string;
    current_page?: number;
    next_page_url?: string;
    [key: string]: unknown;
};

type UseMediasV2Params = {
    limit?: number;
    params?: MediaV2RequestParams | null;
    options?: Parameters<typeof useSWRInfinite<MediaV2Response, Error>>[2];
    shouldFetch?: boolean;
};

type UseMediasV2Return = {
    dataMedias: MediaV2Response[] | undefined;
    loadMoreMedia: () => void;
    mutateMedias: (
        data?: MediaV2Response[],
        shouldRevalidate?: boolean
    ) => Promise<MediaV2Response[] | undefined>;
    mediasError: Error | undefined;
    requestParams: MediaV2RequestParams | null;
    setRequestParams: (
        params:
            | MediaV2RequestParams
            | null
            | ((prev: MediaV2RequestParams | null) => MediaV2RequestParams | null)
    ) => void;
    isLoadingInitialData: boolean;
    isLoadMore: boolean;
    isLoadingMore: boolean;
};

const getKey = (
    pageIndex: number,
    previousPageData: MediaV2Response | null,
    limit: number,
    requestParams: MediaV2RequestParams | null
): [string, string] | null => {
    // No request params
    if (!requestParams) return null;

    // Reached the end - no more pages or empty medias
    if (previousPageData && (!previousPageData.medias?.length || !previousPageData.next_page_url)) {
        return null;
    }

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) {
        return [`${MEDIAS_V2}?per_page=${limit}`, JSON.stringify(requestParams)];
    }

    // Add the page number to the API endpoint
    const nextPage = (previousPageData?.current_page ?? 0) + 1;
    return [`${MEDIAS_V2}?per_page=${limit}&page=${nextPage}`, JSON.stringify(requestParams)];
};

/**
 * Transforms legacy filter params from existing filter components to v2 API format.
 *
 * Mapping:
 * - filter_post_type (string[]) → post_types (string[])
 * - filter_promotion_type ('promoted' | 'non_promoted' | 'all') → promoted (boolean | undefined)
 * - filter_labels (string[]) → labels (string[])
 */
const transformLegacyParams = (
    params: MediaV2RequestParams
): Omit<MediaV2RequestParams, 'filter_post_type' | 'filter_promotion_type' | 'filter_labels'> => {
    const { filter_post_type, filter_promotion_type, filter_labels, ...restParams } = params;

    const transformedParams: MediaV2RequestParams = { ...restParams };

    // Transform filter_post_type → post_types
    if (filter_post_type && filter_post_type.length > 0) {
        // Filter out 'all' since it means no filter
        const filteredTypes = filter_post_type.filter((type) => type !== 'all');
        if (filteredTypes.length > 0) {
            transformedParams.post_types = filteredTypes;
        }
    }

    // Transform filter_promotion_type → promoted (boolean)
    if (filter_promotion_type && filter_promotion_type !== 'all') {
        transformedParams.promoted = filter_promotion_type === 'promoted';
    }

    // Transform filter_labels → labels
    if (filter_labels && filter_labels.length > 0) {
        transformedParams.labels = filter_labels;
    }

    return transformedParams;
};

/**
 * Computes start and end dates in Zulu format (ISO 8601) for the v2 API.
 * The v2 API always requires explicit start and end dates.
 */
const computeDateRange = (
    date: { from: Date; to: Date },
    period: string
): { start: string; end: string } => {
    let startDate: dayjs.Dayjs;
    let endDate: dayjs.Dayjs;

    if (period === CUSTOM) {
        startDate = dayjs(date.from).startOf('day');
        endDate = dayjs(date.to).endOf('day');
    } else {
        // Compute dates from period name
        const currentDayTz = dayjs();
        const minDateTz = currentDayTz.subtract(2, 'year');
        const periodDates = getDatePeriods({
            minDateTz,
            currentDayTz,
            period: period as Parameters<typeof getDatePeriods>[0]['period']
        });
        startDate = periodDates.from;
        endDate = periodDates.to;
    }

    return {
        start: startDate.toISOString(),
        end: endDate.toISOString()
    };
};

export const useMediasV2 = ({
    limit = 20,
    params = null,
    options = {},
    shouldFetch = true
}: UseMediasV2Params = {}): UseMediasV2Return => {
    const [requestParams, setRequestParams] = useState<MediaV2RequestParams | null>(params);

    // Get date/period from global datepicker store
    const { date, period } = useDatepickerGlobalStore((state) => state);

    // Compute dates in Zulu format - v2 API always requires start and end
    const dateRange = useMemo(() => computeDateRange(date, period), [date, period]);

    // Merge request params with computed dates and transform legacy filter params to v2 format
    // Filter out 'period' as v2 API uses explicit start/end dates computed from the datepicker
    const finalRequestParams = useMemo(() => {
        if (!requestParams) return null;

        // Remove period from params - v2 uses computed start/end instead
        const { period: _period, ...paramsWithoutPeriod } = requestParams;

        const transformedParams = transformLegacyParams(paramsWithoutPeriod);

        return {
            ...transformedParams,
            start: dateRange.start,
            end: dateRange.end
        };
    }, [requestParams, dateRange]);

    // Determine if we should actually fetch: shouldFetch must be true AND we have valid params
    const canFetch = shouldFetch && finalRequestParams !== null;

    const {
        data: dataMedias,
        size,
        setSize,
        mutate: mutateMedias,
        error: mediasError
    } = useSWRInfinite<MediaV2Response, Error>(
        (pageIndex, previousPageData) =>
            canFetch ? getKey(pageIndex, previousPageData, limit, finalRequestParams) : null,
        ([url, params]: [string, string]) =>
            get<{ data: MediaV2ApiResponse }>(url, JSON.parse(params)).then((res) => {
                const apiResponse = res.data;
                // Transform API response to match expected format for PostsList
                // API returns: { data: [...], meta: { current_page, last_page, ... } }
                // Expected:   { medias: [...], current_page, next_page_url }
                return {
                    medias: apiResponse.data,
                    current_page: apiResponse.meta.current_page,
                    next_page_url:
                        apiResponse.meta.current_page < apiResponse.meta.last_page
                            ? `page=${apiResponse.meta.current_page + 1}`
                            : undefined
                };
            }),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    // Only show loading if we're actually fetching (canFetch is true)
    const isLoadingInitialData = canFetch && !dataMedias && !mediasError;
    const isLoadingMore = canFetch && size > 0 && typeof dataMedias?.[size - 1] === 'undefined';

    return {
        dataMedias,
        loadMoreMedia: () => setSize(size + 1), // Function to load more media (load more button)
        mutateMedias,
        mediasError,
        requestParams,
        setRequestParams, // Setter to change parameters in the url
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        isLoadMore:
            dataMedias && typeof dataMedias[dataMedias.length - 1]?.next_page_url === 'string', // Check if there is a next page to activate the load more button
        isLoadingMore // Check if the load more is loading
    };
};
